/**
 * Page component for /services/hiatal-hernia-surgery.
 *
 * Layout mirrors the revision spokes (article width, banner, jump nav, FAQ
 * list, related pages, references). The "what is a hiatal hernia" section
 * pairs each hernia-type description with its diagram in a side column.
 */
import { Fragment } from "react";
import { Link } from "@tanstack/react-router";
import { ImageLightbox } from "@/components/site/ImageLightbox";
import { hiatalTypeGalleries } from "@/lib/hiatal-type-galleries";
import { ProcedureFigure } from "@/components/site/ProcedureFigure";
import { MedicalReviewBanner } from "@/components/site/MedicalReview";
import { TelehealthCta } from "@/components/site/TelehealthCta";
import { SectionCta } from "@/components/site/SectionCta";
import { WeightLossOptionsButtons } from "@/components/site/WeightLossOptionsButtons";
import { LastUpdated } from "@/components/site/LastUpdated";
import { HIATAL_RESPONSIVE_IMAGES } from "@/lib/hiatal-responsive";
import {
  HIATAL_FIGURES,
  HIATAL_SLEEVE_SCAN,
  HIATAL_SLEEVE_SCAN_SECOND,
  HIATAL_TYPE1_REPAIR,
  HIATAL_TYPE1_ESOPHAGUS,
  HIATAL_TYPE1_COMPLETED,
  HIATAL_TYPE1_WEIGHT,
  HIATAL_TYPE2_SLEEVE_SCAN,
  HIATAL_TYPE2_PATIENT_STORY,
  HIATAL_TYPE3_SLEEVE_SCAN,
  HIATAL_TYPE3_SLEEVE_SCAN_SECOND,
  HIATAL_TYPE3_PATIENT_STORY,
  HIATAL_TYPE4_PATIENT_STORY,
  HIATAL_TYPE4_UPPER_GI,
  HIATAL_RECURRENT_SCAR_TISSUE,
  HIATAL_RECURRENT_COMPLETED_REPAIR,
  HIATAL_RECURRENT_REDO_2,
  HIATAL_RECURRENT_REDO_2_COMPLETE,
  HIATAL_PAGE,
  type HiatalFigure,
  type HiatalSection,
} from "@/lib/hiatal-hernia";
import { ES_HIATAL_FIGURES, ES_HIATAL_PAGE, ES_HIATAL_SLEEVE_SCAN, ES_HIATAL_SLEEVE_SCAN_SECOND, ES_HIATAL_TYPE1_REPAIR, ES_HIATAL_TYPE1_ESOPHAGUS, ES_HIATAL_TYPE1_COMPLETED, ES_HIATAL_TYPE1_WEIGHT, ES_HIATAL_TYPE2_SLEEVE_SCAN, ES_HIATAL_TYPE2_PATIENT_STORY, ES_HIATAL_TYPE3_SLEEVE_SCAN, ES_HIATAL_TYPE3_SLEEVE_SCAN_SECOND, ES_HIATAL_TYPE3_PATIENT_STORY, ES_HIATAL_TYPE4_PATIENT_STORY, ES_HIATAL_TYPE4_UPPER_GI, ES_HIATAL_RECURRENT_SCAR_TISSUE, ES_HIATAL_RECURRENT_COMPLETED_REPAIR, ES_HIATAL_RECURRENT_REDO_2, ES_HIATAL_RECURRENT_REDO_2_COMPLETE } from "@/lib/es-hiatal-hernia";
import hiatalIntroFundusEn from "@/assets/hiatal-intro-fundus-en-990.webp.asset.json";
import hiatalIntroFundusEs from "@/assets/hiatal-intro-fundus-es-990.webp.asset.json";
import { HIATAL_BLURS } from "@/lib/hiatal-blur-data";

import normalWebp800 from "@/assets/hiatal-normal-v3-800.webp.asset.json";
import normalWebp1200 from "@/assets/hiatal-normal-v3-1200.webp.asset.json";
import normalJpg800 from "@/assets/hiatal-normal-v3-800.jpg.asset.json";
import normalJpg1200 from "@/assets/hiatal-normal-v3-1200.jpg.asset.json";
import esNormalWebp800 from "@/assets/hiatal-normal-es-v3-800.webp.asset.json";
import esNormalWebp1200 from "@/assets/hiatal-normal-es-v3-1200.webp.asset.json";
import esNormalJpg800 from "@/assets/hiatal-normal-es-v3-800.jpg.asset.json";
import esNormalJpg1200 from "@/assets/hiatal-normal-es-v3-1200.jpg.asset.json";
import type1Webp800 from "@/assets/hiatal-type-1-v3-800.webp.asset.json";
import type1Webp1200 from "@/assets/hiatal-type-1-v3-1200.webp.asset.json";
import type1Jpg800 from "@/assets/hiatal-type-1-v3-800.jpg.asset.json";
import type1Jpg1200 from "@/assets/hiatal-type-1-v3-1200.jpg.asset.json";
import esType1Webp800 from "@/assets/hiatal-type-1-es-v3-800.webp.asset.json";
import esType1Webp1200 from "@/assets/hiatal-type-1-es-v3-1200.webp.asset.json";
import esType1Jpg800 from "@/assets/hiatal-type-1-es-v3-800.jpg.asset.json";
import esType1Jpg1200 from "@/assets/hiatal-type-1-es-v3-1200.jpg.asset.json";
import esType2Webp800 from "@/assets/hiatal-type-2-es-v3-800.webp.asset.json";
import esType2Webp1200 from "@/assets/hiatal-type-2-es-v3-1200.webp.asset.json";
import esType2Jpg800 from "@/assets/hiatal-type-2-es-v3-800.jpg.asset.json";
import esType2Jpg1200 from "@/assets/hiatal-type-2-es-v3-1200.jpg.asset.json";
import type2Webp800 from "@/assets/hiatal-type-2-v3-800.webp.asset.json";
import type2Webp1200 from "@/assets/hiatal-type-2-v3-1200.webp.asset.json";
import type2Jpg800 from "@/assets/hiatal-type-2-v3-800.jpg.asset.json";
import type2Jpg1200 from "@/assets/hiatal-type-2-v3-1200.jpg.asset.json";
import type3Webp800 from "@/assets/hiatal-type-3-v3-800.webp.asset.json";
import type3Webp1200 from "@/assets/hiatal-type-3-v3-1200.webp.asset.json";
import type3Jpg800 from "@/assets/hiatal-type-3-v3-800.jpg.asset.json";
import type3Jpg1200 from "@/assets/hiatal-type-3-v3-1200.jpg.asset.json";
import esType3Webp800 from "@/assets/hiatal-type-3-es-v3-800.webp.asset.json";
import esType3Webp1200 from "@/assets/hiatal-type-3-es-v3-1200.webp.asset.json";
import esType3Jpg800 from "@/assets/hiatal-type-3-es-v3-800.jpg.asset.json";
import esType3Jpg1200 from "@/assets/hiatal-type-3-es-v3-1200.jpg.asset.json";
import type4Webp800 from "@/assets/hiatal-type-4-v3-800.webp.asset.json";
import type4Webp1200 from "@/assets/hiatal-type-4-v3-1200.webp.asset.json";
import type4Jpg800 from "@/assets/hiatal-type-4-v3-800.jpg.asset.json";
import type4Jpg1200 from "@/assets/hiatal-type-4-v3-1200.jpg.asset.json";
import esType4Webp800 from "@/assets/hiatal-type-4-es-v3-800.webp.asset.json";
import esType4Webp1200 from "@/assets/hiatal-type-4-es-v3-1200.webp.asset.json";
import esType4Jpg800 from "@/assets/hiatal-type-4-es-v3-800.jpg.asset.json";
import esType4Jpg1200 from "@/assets/hiatal-type-4-es-v3-1200.jpg.asset.json";
import sleeveScanWebp600 from "@/assets/sleeve-hiatal-type-1-600.webp.asset.json";
import sleeveScanWebp1141 from "@/assets/sleeve-hiatal-type-1-1141.webp.asset.json";
import sleeveScanJpg600 from "@/assets/sleeve-hiatal-type-1-600.jpg.asset.json";
import sleeveScanJpg1141 from "@/assets/sleeve-hiatal-type-1-1141.jpg.asset.json";
import esSleeveScanWebp600 from "@/assets/sleeve-hiatal-type-1-es-600.webp.asset.json";
import esSleeveScanWebp1141 from "@/assets/sleeve-hiatal-type-1-es-1141.webp.asset.json";
import esSleeveScanJpg600 from "@/assets/sleeve-hiatal-type-1-es-600.jpg.asset.json";
import esSleeveScanJpg1141 from "@/assets/sleeve-hiatal-type-1-es-1141.jpg.asset.json";
import secondScanWebp800 from "@/assets/sleeve-hiatal-type-1-second-en-800.webp.asset.json";
import secondScanWebp1254 from "@/assets/sleeve-hiatal-type-1-second-en-1254.webp.asset.json";
import secondScanJpg800 from "@/assets/sleeve-hiatal-type-1-second-en-800.jpg.asset.json";
import secondScanJpg1254 from "@/assets/sleeve-hiatal-type-1-second-en-1254.jpg.asset.json";
import secondEsScanWebp800 from "@/assets/sleeve-hiatal-type-1-second-es-800.webp.asset.json";
import secondEsScanWebp1254 from "@/assets/sleeve-hiatal-type-1-second-es-1254.webp.asset.json";
import secondEsScanJpg800 from "@/assets/sleeve-hiatal-type-1-second-es-800.jpg.asset.json";
import secondEsScanJpg1254 from "@/assets/sleeve-hiatal-type-1-second-es-1254.jpg.asset.json";
import type2ScanWebp800 from "@/assets/hiatal-type-2-sleeve-en-800.webp.asset.json";
import type2ScanWebp1448 from "@/assets/hiatal-type-2-sleeve-en-1448.webp.asset.json";
import type2ScanJpg800 from "@/assets/hiatal-type-2-sleeve-en-800.jpg.asset.json";
import type2ScanJpg1448 from "@/assets/hiatal-type-2-sleeve-en-1448.jpg.asset.json";
import esType2ScanWebp800 from "@/assets/hiatal-type-2-sleeve-es-800.webp.asset.json";
import esType2ScanWebp1448 from "@/assets/hiatal-type-2-sleeve-es-1448.webp.asset.json";
import esType2ScanJpg800 from "@/assets/hiatal-type-2-sleeve-es-800.jpg.asset.json";
import esType2ScanJpg1448 from "@/assets/hiatal-type-2-sleeve-es-1448.jpg.asset.json";
import type3ScanWebp800 from "@/assets/hiatal-type-3-sleeve-en-800.webp.asset.json";
import type3ScanWebp1087 from "@/assets/hiatal-type-3-sleeve-en-1087.webp.asset.json";
import type3ScanJpg800 from "@/assets/hiatal-type-3-sleeve-en-800.jpg.asset.json";
import type3ScanJpg1087 from "@/assets/hiatal-type-3-sleeve-en-1087.jpg.asset.json";
import esType3ScanWebp800 from "@/assets/hiatal-type-3-sleeve-es-800.webp.asset.json";
import esType3ScanWebp1086 from "@/assets/hiatal-type-3-sleeve-es-1086.webp.asset.json";
import esType3ScanJpg800 from "@/assets/hiatal-type-3-sleeve-es-800.jpg.asset.json";
import esType3ScanJpg1086 from "@/assets/hiatal-type-3-sleeve-es-1086.jpg.asset.json";
import type3SecondScanWebp800 from "@/assets/hiatal-type-3-second-en-800.webp.asset.json";
import type3SecondScanWebp1122 from "@/assets/hiatal-type-3-second-en-1122.webp.asset.json";
import type3SecondScanJpg800 from "@/assets/hiatal-type-3-second-en-800.jpg.asset.json";
import type3SecondScanJpg1122 from "@/assets/hiatal-type-3-second-en-1122.jpg.asset.json";
import esType3SecondScanWebp800 from "@/assets/hiatal-type-3-second-es-800.webp.asset.json";
import esType3SecondScanWebp1122 from "@/assets/hiatal-type-3-second-es-1122.webp.asset.json";
import esType3SecondScanJpg800 from "@/assets/hiatal-type-3-second-es-800.jpg.asset.json";
import esType3SecondScanJpg1122 from "@/assets/hiatal-type-3-second-es-1122.jpg.asset.json";

const RESPONSIVE: Record<
  HiatalFigure["key"],
  { webp800: string; webp1200: string; jpg800: string; jpg1200: string; blur: string }
> = {
  normal: {
    webp800: normalWebp800.url,
    webp1200: normalWebp1200.url,
    jpg800: normalJpg800.url,
    jpg1200: normalJpg1200.url,
    blur: HIATAL_BLURS.normal,
  },
  type1: {
    webp800: type1Webp800.url,
    webp1200: type1Webp1200.url,
    jpg800: type1Jpg800.url,
    jpg1200: type1Jpg1200.url,
    blur: HIATAL_BLURS.type1,
  },
  type2: {
    webp800: type2Webp800.url,
    webp1200: type2Webp1200.url,
    jpg800: type2Jpg800.url,
    jpg1200: type2Jpg1200.url,
    blur: HIATAL_BLURS.type2,
  },
  type3: {
    webp800: type3Webp800.url,
    webp1200: type3Webp1200.url,
    jpg800: type3Jpg800.url,
    jpg1200: type3Jpg1200.url,
    blur: HIATAL_BLURS.type3,
  },
  type4: {
    webp800: type4Webp800.url,
    webp1200: type4Webp1200.url,
    jpg800: type4Jpg800.url,
    jpg1200: type4Jpg1200.url,
    blur: HIATAL_BLURS.type4,
  },
  intro: {
    webp800: hiatalIntroFundusEn.url,
    webp1200: hiatalIntroFundusEn.url,
    jpg800: hiatalIntroFundusEn.url,
    jpg1200: hiatalIntroFundusEn.url,
    blur: "none",
  },
};

const FIGURE_SIZES = "(min-width: 768px) 22.125rem, min(calc(100vw - 3rem), 22.125rem)";
const PAIR_SIZES = "(min-width: 768px) 15.5rem, calc((100vw - 4rem) / 2)";
const TRIPLE_SIZES = "(min-width: 768px) 19.5rem, calc((100vw - 5rem) / 3)";
const SLEEVE_SCAN_RESPONSIVE = {
  webp800: sleeveScanWebp600.url,
  webp1200: sleeveScanWebp1141.url,
  jpg800: sleeveScanJpg600.url,
  jpg1200: sleeveScanJpg1141.url,
  blur: "url(data:image/webp;base64,UklGRkwBAABXRUJQVlA4IEABAACwBwCdASogACcAPxFys1MsJiSiqrgMAYAiCUAZ0p4HTvP/qYw57OgeMIlDIUMzBkWYk5jDTW/4S/yq+gtsxADwVTevP8cAAP7vrjkdsLqD5laPHlO6sWh8ZJFp0ZXEFUXS4lub8sMb0VXbDD1dsVRPxTZq1TKyg8UFZq2LwY35YvqNyZfZDDVyom7JjXeKz19kCLEvjg+oRgayNI96lqDaPQ5RPzPTPXuRseH/x7nmJahaiR6rGjDwwRCELzuywmJwMoD1Qvtb85jbXrqgGBoZsP49D8s8jDSxE+ljgPATXHN16VAKYjp2e73q+b3jIqfl0XhudoL6NwWGyVFsJiRUShfo5fu5qNpSv0qhPcqvgxkGNYHfbfhNB8iocZhebKzajlkPuVtfxLpbjIDmpSOv5MunJln8MIlZOOkCg3gAAA==)",
};
const ES_SLEEVE_SCAN_RESPONSIVE = {
  webp800: esSleeveScanWebp600.url,
  webp1200: esSleeveScanWebp1141.url,
  jpg800: esSleeveScanJpg600.url,
  jpg1200: esSleeveScanJpg1141.url,
  blur: "url(data:image/webp;base64,UklGRsIAAABXRUJQVlA4ILYAAADQBACdASoUABgAPwF0rVKrJqOiqAqpYCAJagDM0YyKUTZGg7pTBkkcB2PRuJM8AAD+T2HvXo1A6aP+pcSxj4v3ZzWf82J7csSKKIeorZe2nr1NqkgBaDTBmkMRFTADeItONJ9xEOzRlW3f/CAn/NrqCFb1vtv/ZfjDYL9nSR/YBXCkJQBu3NO/dAew7Rt0ayO+kK9AWLbTz1ow6ZbLt7fqWbVJco6WQGlTdlKdHNXIoQ7gx6AgAA==)",
};
const SECOND_SCAN_RESPONSIVE = {
  webp800: secondScanWebp800.url,
  webp1200: secondScanWebp1254.url,
  jpg800: secondScanJpg800.url,
  jpg1200: secondScanJpg1254.url,
  blur: "none",
};
const SECOND_ES_SCAN_RESPONSIVE = {
  webp800: secondEsScanWebp800.url,
  webp1200: secondEsScanWebp1254.url,
  jpg800: secondEsScanJpg800.url,
  jpg1200: secondEsScanJpg1254.url,
  blur: "none",
};
const TYPE2_SCAN_RESPONSIVE = {
  webp800: type2ScanWebp800.url,
  webp1200: type2ScanWebp1448.url,
  jpg800: type2ScanJpg800.url,
  jpg1200: type2ScanJpg1448.url,
  blur: "none",
};
const ES_TYPE2_SCAN_RESPONSIVE = {
  webp800: esType2ScanWebp800.url,
  webp1200: esType2ScanWebp1448.url,
  jpg800: esType2ScanJpg800.url,
  jpg1200: esType2ScanJpg1448.url,
  blur: "none",
};
const TYPE3_SCAN_RESPONSIVE = {
  webp800: type3ScanWebp800.url,
  webp1200: type3ScanWebp1087.url,
  jpg800: type3ScanJpg800.url,
  jpg1200: type3ScanJpg1087.url,
  blur: "none",
};
const ES_TYPE3_SCAN_RESPONSIVE = {
  webp800: esType3ScanWebp800.url,
  webp1200: esType3ScanWebp1086.url,
  jpg800: esType3ScanJpg800.url,
  jpg1200: esType3ScanJpg1086.url,
  blur: "none",
};
const SECOND_TYPE3_SCAN_RESPONSIVE = {
  webp800: type3SecondScanWebp800.url,
  webp1200: type3SecondScanWebp1122.url,
  jpg800: type3SecondScanJpg800.url,
  jpg1200: type3SecondScanJpg1122.url,
  blur: "none",
};
const SECOND_ES_TYPE3_SCAN_RESPONSIVE = {
  webp800: esType3SecondScanWebp800.url,
  webp1200: esType3SecondScanWebp1122.url,
  jpg800: esType3SecondScanJpg800.url,
  jpg1200: esType3SecondScanJpg1122.url,
  blur: "none",
};
/** Spanish-language variants; the type I and type II diagrams differ from English. */
const ES_RESPONSIVE: Partial<Record<HiatalFigure["key"], Partial<(typeof RESPONSIVE)[HiatalFigure["key"]]>>> = {
  normal: {
    webp800: esNormalWebp800.url,
    webp1200: esNormalWebp1200.url,
    jpg800: esNormalJpg800.url,
    jpg1200: esNormalJpg1200.url,
  },
  type1: {
    webp800: esType1Webp800.url,
    webp1200: esType1Webp1200.url,
    jpg800: esType1Jpg800.url,
    jpg1200: esType1Jpg1200.url,
    blur: HIATAL_BLURS.esType1,
  },
  type2: {
    webp800: esType2Webp800.url,
    webp1200: esType2Webp1200.url,
    jpg800: esType2Jpg800.url,
    jpg1200: esType2Jpg1200.url,
    blur: HIATAL_BLURS.esType2,
  },
  type3: {
    webp800: esType3Webp800.url,
    webp1200: esType3Webp1200.url,
    jpg800: esType3Jpg800.url,
    jpg1200: esType3Jpg1200.url,
    blur: HIATAL_BLURS.esType3,
  },
  type4: {
    webp800: esType4Webp800.url,
    webp1200: esType4Webp1200.url,
    jpg800: esType4Jpg800.url,
    jpg1200: esType4Jpg1200.url,
    blur: HIATAL_BLURS.esType4,
  },
};

function IntroFigure({ figure, lang, gallery, captionId }: { figure: HiatalFigure; lang: "en" | "es"; gallery?: HiatalFigure[]; captionId: string }) {
  const spanish = lang === "es";
  const responsive = HIATAL_RESPONSIVE_IMAGES[figure.full];
  const firstViewportImage = captionId === "hiatal-intro-caption";
  return (
    <figure className="m-0 mx-auto w-full max-w-[22.125rem] md:max-w-[29.5rem]" aria-label={figure.alt}>
      <ImageLightbox
        fullSrc={figure.full}
        fullWidth={figure.width}
        fullHeight={figure.height}
        alt={figure.alt}
        caption={figure.caption}
        images={gallery?.map((image) => ({ src: image.full, width: image.width, height: image.height, alt: image.alt, caption: image.caption }))}
        lang={lang}
        enlargeLabel={spanish ? `Ampliar imagen: ${figure.alt}` : undefined}
        closeLabel={spanish ? "Cerrar imagen ampliada" : undefined}
      >
        <img
          src={responsive?.fallback ?? figure.full}
          srcSet={responsive?.srcSet}
          sizes="(min-width: 768px) 472px, min(calc(100vw - 3rem), 354px)"
          width={figure.width}
          height={figure.height}
          alt={figure.alt}
          aria-describedby={captionId}
          className="h-auto w-full rounded-lg bg-muted"
          loading={firstViewportImage ? "eager" : "lazy"}
          fetchPriority={firstViewportImage ? "high" : "low"}
          decoding="async"
        />
      </ImageLightbox>
      <figcaption id={captionId} className="mt-2 text-center text-xs italic text-foreground/70">
        {figure.caption}
      </figcaption>
    </figure>
  );
}

function HiatalFigureBlock({ figureKey, figures = HIATAL_FIGURES, lang = "en", sleeveScan = false, secondScan = false, type1Repair = false, type2Scan = false, type3Scan = false, paired = false, triple = false, gallery }: { gallery?: HiatalFigure[]; figureKey: HiatalFigure["key"]; figures?: Record<HiatalFigure["key"], HiatalFigure>; lang?: "en" | "es"; sleeveScan?: boolean; secondScan?: boolean; type1Repair?: boolean; type2Scan?: boolean; type3Scan?: boolean; paired?: boolean; triple?: boolean }) {
  const figure = type1Repair ? (lang === "es" ? ES_HIATAL_TYPE1_REPAIR : HIATAL_TYPE1_REPAIR) : type3Scan ? (lang === "es" ? ES_HIATAL_TYPE3_SLEEVE_SCAN_SECOND : HIATAL_TYPE3_SLEEVE_SCAN_SECOND) : type2Scan ? (lang === "es" ? ES_HIATAL_TYPE2_SLEEVE_SCAN : HIATAL_TYPE2_SLEEVE_SCAN) : secondScan ? (lang === "es" ? ES_HIATAL_SLEEVE_SCAN_SECOND : HIATAL_SLEEVE_SCAN_SECOND) : sleeveScan ? (lang === "es" ? ES_HIATAL_SLEEVE_SCAN : HIATAL_SLEEVE_SCAN) : figures[figureKey];
  const r = type3Scan ? (lang === "es" ? SECOND_ES_TYPE3_SCAN_RESPONSIVE : SECOND_TYPE3_SCAN_RESPONSIVE) : type2Scan ? (lang === "es" ? ES_TYPE2_SCAN_RESPONSIVE : TYPE2_SCAN_RESPONSIVE) : secondScan ? (lang === "es" ? SECOND_ES_SCAN_RESPONSIVE : SECOND_SCAN_RESPONSIVE) : sleeveScan ? (lang === "es" ? ES_SLEEVE_SCAN_RESPONSIVE : SLEEVE_SCAN_RESPONSIVE) : { ...RESPONSIVE[figureKey], ...(lang === "es" ? ES_RESPONSIVE[figureKey] : undefined) };
  const captionId = `hiatal-${figureKey}${type1Repair ? "-repair" : type3Scan ? "-type3-study" : type2Scan ? "-sleeve-study" : secondScan ? "-second-sleeve" : sleeveScan ? "-sleeve" : ""}-caption`;
  const spanish = lang === "es";
  const type3Story = type3Scan ? (spanish ? [ES_HIATAL_TYPE3_SLEEVE_SCAN_SECOND, ES_HIATAL_TYPE3_SLEEVE_SCAN] : [HIATAL_TYPE3_SLEEVE_SCAN_SECOND, HIATAL_TYPE3_SLEEVE_SCAN]) : undefined;
  const type1Story = type1Repair
    ? spanish ? [ES_HIATAL_TYPE1_REPAIR, ES_HIATAL_TYPE1_ESOPHAGUS, ES_HIATAL_TYPE1_COMPLETED, ES_HIATAL_TYPE1_WEIGHT] : [HIATAL_TYPE1_REPAIR, HIATAL_TYPE1_ESOPHAGUS, HIATAL_TYPE1_COMPLETED, HIATAL_TYPE1_WEIGHT]
    : sleeveScan ? (spanish ? [ES_HIATAL_SLEEVE_SCAN, ES_HIATAL_SLEEVE_SCAN_SECOND] : [HIATAL_SLEEVE_SCAN, HIATAL_SLEEVE_SCAN_SECOND]) : undefined;
  const lightboxStory = gallery ?? type3Story ?? type1Story;
  const sizes = triple ? TRIPLE_SIZES : paired ? PAIR_SIZES : FIGURE_SIZES;
  const responsive = HIATAL_RESPONSIVE_IMAGES[figure.full];
  return (
    <figure className="m-0" aria-label={figure.alt}>
      <ImageLightbox
        fullSrc={figure.full}
        fullWidth={figure.width}
        fullHeight={figure.height}
        alt={figure.alt}
        caption={figure.caption}
        images={lightboxStory?.map((image) => ({ src: image.full, width: image.width, height: image.height, alt: image.alt, caption: image.caption }))}
        lang={lang}
        enlargeLabel={spanish ? `Ampliar imagen: ${figure.alt}` : undefined}
        closeLabel={spanish ? "Cerrar imagen ampliada" : undefined}
      >
        {type1Repair ? (
          <img
            src={responsive?.fallback ?? figure.full}
            srcSet={responsive?.srcSet}
            width={figure.width}
            height={figure.height}
            alt={figure.alt}
            aria-describedby={captionId}
            sizes={TRIPLE_SIZES}
            className="aspect-[3/2] h-auto w-full rounded-lg bg-muted object-contain"
            loading="lazy"
            fetchPriority="low"
            decoding="async"
          />
        ) : (
        <picture className={paired ? `relative block w-full overflow-hidden rounded-lg bg-muted ${type3Scan ? "aspect-[5/4]" : "aspect-[3/2]"}` : undefined}>
            <source type="image/webp" srcSet={`${r.webp800} ${sleeveScan ? 600 : 800}w, ${r.webp1200} ${type3Scan ? figure.width : type2Scan ? 1448 : secondScan ? 1254 : sleeveScan ? 1141 : 1200}w`} sizes={sizes} />
          <img
            src={r.jpg800}
               srcSet={`${r.jpg800} ${sleeveScan ? 600 : 800}w, ${r.jpg1200} ${type3Scan ? figure.width : type2Scan ? 1448 : secondScan ? 1254 : sleeveScan ? 1141 : 1200}w${sleeveScan || secondScan || type2Scan || type3Scan ? "" : `, ${figure.full} ${figure.width}w`}`}
            sizes={sizes}
            width={figure.width}
            height={figure.height}
            alt={figure.alt}
            aria-describedby={captionId}
            className={paired
               ? sleeveScan
                 ? "absolute -top-[14.2857%] left-1/2 h-[142.857%] w-auto max-w-none -translate-x-1/2 bg-cover bg-center"
                  : type3Scan
                    ? "absolute -top-[8.3333%] left-0 h-[156.25%] w-full max-w-none bg-cover bg-center"
                   : "h-full w-full object-contain bg-cover bg-center"
              : "h-auto w-full rounded-lg bg-muted bg-cover bg-center"}
             style={{ backgroundImage: r.blur }}
            loading="lazy"
            fetchPriority="low"
            decoding="async"
          />
        </picture>
        )}
      </ImageLightbox>
      <figcaption id={captionId} className={paired ? "mt-1 line-clamp-2 text-center text-xs italic text-foreground/70 md:mt-2 md:line-clamp-none" : "mt-2 text-center text-xs italic text-foreground/70"}>
        {figure.caption}
      </figcaption>
    </figure>
  );
}

/**
 * Renders body copy with inline conventions: [text](path) links, **bold** and
 * *italic* runs, ^n^ superscript citation links, and ~n~ footnote markers.
 */
function renderCopy(text: string, lang: "en" | "es" = "en") {
  const parts: React.ReactNode[] = [];
  const pattern =
    /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*|\^(\d+(?:,\d+)*)\^|~(\d+)~/g;
  let match: RegExpExecArray | null;
  let lastIndex = 0;
  let key = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    if (match[1] !== undefined) {
      const target = match[2];
      const isExternal = /^https?:\/\//.test(target);
      parts.push(
        isExternal ? (
          <a key={key++} href={target} target="_blank" rel="noopener noreferrer" className="text-brand-deep underline hover:no-underline">
            {match[1]}
          </a>
        ) : (
          <Link key={key++} to={target} preload="intent" className="text-brand-deep underline hover:no-underline">
            {match[1]}
          </Link>
        ),
      );
    } else if (match[3] !== undefined) {
      parts.push(<strong key={key++} className="font-semibold text-foreground">{match[3]}</strong>);
    } else if (match[4] !== undefined) {
      parts.push(<em key={key++} className="italic">{match[4]}</em>);
    } else if (match[5] !== undefined) {
      const refs = match[5].split(",");
      parts.push(
        <sup key={key++}>
          {refs.map((ref, refIndex) => (
            <Fragment key={refIndex}>
              {refIndex > 0 ? "," : null}
              <a href={`#reference-${ref}`} className="text-brand-deep underline hover:no-underline" aria-label={`${lang === "es" ? "Referencia" : "Reference"} ${ref}`}>
                {ref}
              </a>
            </Fragment>
          ))}
        </sup>,
      );
    } else if (match[6] !== undefined) {
      parts.push(
        <sup key={key++}>
          <a
            id={`footnote-ref-${match[6]}`}
            href={`#footnote-${match[6]}`}
            className="text-brand-deep underline hover:no-underline"
            aria-label={`${lang === "es" ? "Nota" : "Footnote"} ${match[6]}`}
          >
            {match[6]}
          </a>
        </sup>,
      );
    }
    lastIndex = pattern.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

const REFLUX_PAIR_SIZES = "(min-width: 640px) 370px, calc((100vw - 4rem) / 2)";

/** Before/after UGI pair: sleeve with hernia and reflux, then post-repair conversion to bypass. */
function HiatalRefluxFigurePair({ lang }: { lang: "en" | "es" }) {
  const preKey = lang === "es" ? "sleeveUgiRefluxPreEs" : "sleeveUgiRefluxPreEn";
  const postKey = lang === "es" ? "sleeveUgiRefluxPostEs" : "sleeveUgiRefluxPostEn";
  return (
    <section
      aria-label={lang === "es" ? "Serie gastrointestinal superior antes y después de la reparación de hernia hiatal con conversión a bypass" : "Upper GI series before and after hiatal hernia repair with conversion to bypass"}
      className="mt-6 grid grid-cols-2 gap-3 md:gap-4"
    >
      <ProcedureFigure
        media={preKey}
        lang={lang}
        lightbox
        previewCropTopPercent={8}
        previewCropBottomPercent={4}
        sizes={REFLUX_PAIR_SIZES}
        imgClassName="h-auto w-full rounded-lg bg-muted"
        captionClassName="mt-2 text-center text-sm text-foreground/70"
      />
      <ProcedureFigure
        media={postKey}
        lang={lang}
        lightbox
        previewCropTopPercent={lang === "es" ? 5 : undefined}
        previewCropBottomPercent={lang === "es" ? 15.4 : undefined}
        sizes={REFLUX_PAIR_SIZES}
        imgClassName="h-auto w-full rounded-lg bg-muted"
        captionClassName="mt-2 text-center text-sm text-foreground/70"
      />
    </section>
  );
}

function SectionBody({ section, figures, lang }: { section: HiatalSection; figures: Record<HiatalFigure["key"], HiatalFigure>; lang: "en" | "es" }) {
  const isRefluxSection = section.id === "reflux-after-sleeve" || section.id === "reflujo-despues-de-manga";
  const isRecurrentSection = section.id === "recurrent-hernias" || section.id === "hernias-recurrentes";
  const recurrentFigure = lang === "es" ? ES_HIATAL_RECURRENT_SCAR_TISSUE : HIATAL_RECURRENT_SCAR_TISSUE;
  const recurrentGallery = lang === "es" ? [ES_HIATAL_RECURRENT_SCAR_TISSUE, ES_HIATAL_RECURRENT_COMPLETED_REPAIR] : [HIATAL_RECURRENT_SCAR_TISSUE, HIATAL_RECURRENT_COMPLETED_REPAIR];
  const recurrentGallery2 = lang === "es" ? [ES_HIATAL_RECURRENT_REDO_2, ES_HIATAL_RECURRENT_REDO_2_COMPLETE] : [HIATAL_RECURRENT_REDO_2, HIATAL_RECURRENT_REDO_2_COMPLETE];
  const subsectionNodes = section.subsections?.map((subsection) => (
    <section key={subsection.id} className="mt-6" aria-labelledby={subsection.id}>
      <h3 id={subsection.id} className="text-[1.05rem] md:text-[1.2rem] font-serif">
        {subsection.title}
      </h3>
      {subsection.paragraphs.map((paragraph) => (
        <p key={paragraph.slice(0, 40)} className="mt-3 text-foreground/80">
          {renderCopy(paragraph, lang)}
        </p>
      ))}
    </section>
  ));
  return (
    <>
      {section.subsectionsBefore ? subsectionNodes : null}
      {section.figure && section.paragraphs ? (
        <div className="relative left-1/2 grid w-[calc(100vw-3rem)] max-w-[960px] -translate-x-1/2 gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,22.125rem)] md:items-start">
          <div>
            {section.paragraphs.map((paragraph) => (
              <p key={paragraph.slice(0, 40)} className="mt-3 first:mt-0 text-foreground/80">
                {renderCopy(paragraph, lang)}
              </p>
            ))}
            {section.afterFigureParagraphs?.map((paragraph) => (
              <p key={paragraph.slice(0, 40)} className="mt-3 text-foreground/80">
                {renderCopy(paragraph, lang)}
              </p>
            ))}
          </div>
          <div className="mx-auto w-full max-w-[22.125rem] md:mx-0 md:sticky md:top-4">
            <HiatalFigureBlock figureKey={section.figure} figures={figures} lang={lang} />
          </div>
        </div>
      ) : (
        section.paragraphs?.map((paragraph, paragraphIndex) => (
          <Fragment key={paragraph.slice(0, 40)}>
            <p className="mt-3 text-foreground/80">
              {renderCopy(paragraph, lang)}
            </p>
            {isRefluxSection && paragraphIndex === 1 ? <HiatalRefluxFigurePair lang={lang} /> : null}
            {isRecurrentSection && paragraphIndex === 6 ? (
              <div className="mx-auto mt-5 grid max-w-3xl gap-4 sm:grid-cols-2">
                <IntroFigure figure={recurrentFigure} lang={lang} gallery={recurrentGallery} captionId="hiatal-recurrent-scar-caption" />
                <IntroFigure figure={recurrentGallery2[0]} lang={lang} gallery={recurrentGallery2} captionId="hiatal-recurrent-redo-caption" />
              </div>
            ) : null}
          </Fragment>
        ))
      )}


      {section.midSubsections?.map((subsection) => (
        <section key={subsection.id} className="mt-6" aria-labelledby={subsection.id}>
          <h3 id={subsection.id} className="text-[1.05rem] md:text-[1.2rem] font-serif">
            {subsection.title}
          </h3>
          {subsection.paragraphs.map((paragraph) => (
            <p key={paragraph.slice(0, 40)} className="mt-3 text-foreground/80">
              {renderCopy(paragraph, lang)}
            </p>
          ))}
        </section>
      ))}

      {section.typeBlocks?.map((block) => (
        <section key={block.title} className="mt-5" aria-labelledby={`hiatal-${block.figure}-heading`}>
          <div>
            <h3 id={`hiatal-${block.figure}-heading`} className="text-[1.05rem] md:text-[1.2rem] font-serif">{block.title}</h3>
            <p className="mt-2 text-foreground/80">{renderCopy(block.text, lang)}</p>
          </div>
          <div className="relative left-1/2 mt-4 w-[calc(100vw-3rem)] max-w-[22.125rem] -translate-x-1/2">
            <HiatalFigureBlock
              figureKey={block.figure}
              figures={figures}
              lang={lang}
              gallery={hiatalTypeGalleries(lang)[block.figure]}
            />
          </div>
        </section>
      ))}

      {section.endParagraphs?.map((paragraph) => (
        <p key={paragraph.slice(0, 40)} className="mt-3 text-foreground/80">
          {renderCopy(paragraph, lang)}
        </p>
      ))}


      {section.bulletGroups?.map((group) => (
        <Fragment key={group.label ?? group.items[0]}>
          {group.label ? <p className="mt-3 text-foreground/80">{group.label}</p> : null}
          <ul className="mt-2 list-disc space-y-1 pl-6 text-foreground/80">
            {group.items.map((item) => (
              <li key={item.slice(0, 40)}>{renderCopy(item, lang)}</li>
            ))}
          </ul>
        </Fragment>
      ))}

      {section.afterBulletFigure ? (
        <div className="relative left-1/2 mt-4 w-[calc(100vw-3rem)] max-w-[22.125rem] -translate-x-1/2">
          <IntroFigure figure={figures[section.afterBulletFigure]} lang={lang} captionId={`hiatal-${section.id}-bullet-figure-caption`} />
        </div>
      ) : null}

      {!section.subsectionsBefore ? subsectionNodes : null}

      {section.afterBulletParagraphs?.map((paragraph) => (
        <p key={paragraph.slice(0, 40)} className="mt-3 text-foreground/80">
          {renderCopy(paragraph, lang)}
        </p>
      ))}
    </>
  );
}

export function HiatalHerniaPage() {
  return <HiatalHerniaArticle />;
}

export function EsHiatalHerniaPage() {
  return <HiatalHerniaArticle lang="es" />;
}

function HiatalHerniaArticle({ lang = "en" }: { lang?: "en" | "es" }) {
  const spanish = lang === "es";
  const page = spanish ? ES_HIATAL_PAGE : HIATAL_PAGE;
  const figures = spanish ? ES_HIATAL_FIGURES : HIATAL_FIGURES;
  return (
    <article lang={lang} className="mx-auto max-w-3xl px-6 md:px-12 lg:px-32 xl:px-44 2xl:px-56 pt-4 pb-10 md:text-[1.125rem] leading-[1.6]">
      <header>
        <nav aria-label="Breadcrumb" className="text-sm">
          <Link to={spanish ? "/es" : "/services"} preload="intent" className="text-brand-deep underline hover:no-underline">
            {spanish ? "Servicios para bajar de peso" : "Weight-loss services"}
          </Link>
        </nav>
        <h1 className="mt-2 text-[2.025rem] md:text-[2.7rem] font-serif text-center">{page.h1}</h1>
        <p className="mt-1 text-foreground/70 italic text-center">{page.kicker}</p>
      </header>

      <MedicalReviewBanner lang={lang} className="mt-4" />

      {page.intro.map((paragraph) => (
        <p key={paragraph.slice(0, 40)} className="mt-3 text-foreground/80">
          {renderCopy(paragraph, lang)}
        </p>
      ))}

      <nav aria-label={spanish ? "En esta página" : "On this page"} className="mt-5 text-sm">
        <p className="font-semibold text-foreground">{spanish ? "En esta página" : "On this page"}</p>
        <ul className="mt-2 grid list-disc gap-x-6 gap-y-1 pl-5 sm:grid-cols-2">
          {page.sections.filter((section) => !["serving-virginia", "atencion-en-virginia"].includes(section.id)).map((section) => (
            <li key={section.id}><a href={`#${section.id}`} className="text-brand-deep underline hover:no-underline">{section.heading}</a></li>
          ))}
          <li><a href="#faq" className="text-brand-deep underline hover:no-underline">{spanish ? "Preguntas frecuentes" : "FAQ"}</a></li>
        </ul>
      </nav>

      {spanish ? (
        <div className="mt-6 text-center">
          <Link to="/es/contacto" className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90">
            Consulta por Telesalud
          </Link>
        </div>
      ) : <TelehealthCta />}

      {page.sections.map((section, index) => {
        const isRecurrent = section.id === (spanish ? "hernias-recurrentes" : "recurrent-hernias");
        const heading = (
          <>
            {isRecurrent ? (
              <SectionCta to={spanish ? "/es/contacto" : "/appointments"} label={`cta_hiatal_before_recurrent${spanish ? "_es" : ""}`}>
                {spanish ? "Pida una cita con el Dr. Fitzer" : "Make an appointment with Dr. Fitzer"}
              </SectionCta>
            ) : null}
            <h2 id={section.id} className="mt-6 scroll-mt-24 text-[1.35rem] md:text-[1.6875rem] font-serif">
              <span id={`${section.id}-heading`}>{section.heading}</span>
            </h2>
          </>
        );

        return (
        <section
          key={section.id}
          aria-labelledby={`${section.id}-heading`}
          className={index === 0 ? undefined : "defer-render"}
        >
          {section.figure && section.paragraphs ? (
            <div className="relative left-1/2 w-[calc(100vw-3rem)] max-w-[960px] -translate-x-1/2">{heading}</div>
          ) : (
            heading
          )}

            <SectionBody section={section} figures={figures} lang={lang} />
            {section.id === (spanish ? "cuando-se-recomienda-la-cirugia" : "when-surgery-is-recommended") ? (
              <SectionCta to={spanish ? "/es/contacto" : "/appointments"} label={`cta_hiatal_after_recommendation${spanish ? "_es" : ""}`}>
                {spanish ? "Averigüe si la reparación es adecuada para usted" : "Find Out if Repair Is Right for You"}
              </SectionCta>
            ) : section.id === (spanish ? "recuperacion" : "recovery") ? (
              <SectionCta to={spanish ? "/es/contacto" : "/appointments"} label={`cta_hiatal_after_recovery${spanish ? "_es" : ""}`}>
                {spanish ? "Pregunte cómo sería su recuperación" : "Ask What Recovery Looks Like for You"}
              </SectionCta>
            ) : section.id === (spanish ? "seguro" : "insurance") ? (
              <SectionCta to={spanish ? "/es/cobertura-de-seguro" : "/resources/insurance-coverage"} label={`cta_hiatal_after_insurance${spanish ? "_es" : ""}`}>
                {spanish ? "Revise su cobertura" : "Check Your Coverage"}
              </SectionCta>
            ) : spanish && section.id === "atencion-en-virginia" ? (
              <>
                <p className="mt-3 text-foreground/80">
                  ¿Preguntas sobre la cirugía de hernia hiatal? Llame a nuestro consultorio de Herndon al{" "}
                  <a href="tel:+17037099771" aria-label="Llame a Virginia Bariatric Surgery al 703-709-9771" className="font-medium text-brand-logo hover:underline">
                    703-709-9771
                  </a>
                  .
                </p>
                <SectionCta to="/es/contacto" label="cta_hiatal_location_es">Solicite una Consulta</SectionCta>
              </>
            ) : null}
        </section>
        );
      })}


      <h2 id="faq" className="mt-6 scroll-mt-24 text-[1.35rem] md:text-[1.6875rem] font-serif">
        {spanish ? "Preguntas frecuentes" : "Frequently asked questions"}
      </h2>
      <dl className="defer-render mt-2 space-y-3 text-foreground/80">
        {page.faqs.map((faq) => (
          <div key={faq.question}>
            <dt className="font-medium text-foreground">{faq.question}</dt>
            <dd className="mt-1">
              {faq.answer.split("\n\n").map((para, i) => (
                <p key={i} className={i > 0 ? "mt-2" : undefined}>{para}</p>
              ))}
            </dd>
          </div>
        ))}
      </dl>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">{spanish ? "Páginas relacionadas" : "Related pages"}</h2>
      <ul className="mt-2 list-disc space-y-1 pl-6 text-foreground/80">
        {page.relatedPages.map((related) => (
          <li key={related.to}>
            <Link to={related.to} preload="intent" className="text-brand-deep underline hover:no-underline">
              {related.label}
            </Link>{" "}
            — {related.note}
          </li>
        ))}
      </ul>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">{spanish ? "Dé el siguiente paso" : "Take the next step"}</h2>
      <p className="mt-3 text-foreground/80">{page.closing}</p>

      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link
          to={spanish ? "/es/contacto" : "/appointments"}
          className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          {spanish ? "Solicitar una Consulta" : "Request a consultation"}
        </Link>
        {spanish ? null : <WeightLossOptionsButtons />}
      </div>

      <p className="mt-6 text-sm italic text-foreground/70">{page.byline}</p>

      {page.footnotes?.length ? (
        <section aria-labelledby="footnotes-heading" className="defer-render">
          <h2 id="footnotes-heading" className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
            {spanish ? "Notas" : "Notes"}
          </h2>
          <ol className="mt-2 list-decimal space-y-2 pl-6 text-sm text-foreground/70">
            {page.footnotes.map((footnote, index) => (
              <li key={footnote.slice(0, 40)} id={`footnote-${index + 1}`} className="scroll-mt-24">
                {renderCopy(footnote, lang)}{" "}
                <a
                  href={`#footnote-ref-${index + 1}`}
                  className="text-brand-deep underline hover:no-underline"
                  aria-label={spanish ? "Volver al texto" : "Back to text"}
                >
                  ↩
                </a>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      <section aria-labelledby="medical-references" className="defer-render">
        <h2 id="medical-references" className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
          {spanish ? "Referencias médicas" : "Medical references"}
        </h2>
        <ol className="mt-2 list-decimal space-y-2 pl-6 text-sm text-foreground/70">
          {page.references.map((reference, index) => (
            <li key={reference.slice(0, 40)} id={`reference-${index + 1}`}>
              {renderCopy(reference, lang)}
            </li>
          ))}
        </ol>
      </section>
      <LastUpdated className="mt-10" />
    </article>
  );
}
